import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { QuizLeaderboardEntry } from '../../types/quiz';
import { useAuth } from '../../contexts/AuthContext';

interface LeaderboardTableProps {
  entries: QuizLeaderboardEntry[];
  maxEntries?: number;
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({ entries, maxEntries = 100 }) => {
  const { authState } = useAuth();
  const { user } = authState;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const displayEntries = entries.slice(0, maxEntries);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMedalIcon = (rank: number) => {
    const colors = ['#FFD700', '#C0C0C0', '#CD7F32']; // Gold, Silver, Bronze
    if (rank <= 3) {
      return <TrophyIcon sx={{ color: colors[rank - 1], fontSize: 28 }} />;
    }
    return null;
  };

  const isCurrentUser = (entry: QuizLeaderboardEntry) => {
    return user && entry.userId === user.id;
  };

  if (isMobile) {
    // Mobile-friendly card layout
    return (
      <Box>
        {displayEntries.map((entry) => (
          <Paper
            key={entry.userId}
            elevation={2}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: isCurrentUser(entry) ? 'primary.light' : 'background.paper',
              borderLeft: isCurrentUser(entry) ? `4px solid ${theme.palette.primary.main}` : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {entry.rank <= 3 ? (
                  getMedalIcon(entry.rank)
                ) : (
                  <Typography variant="h6" fontWeight="bold">
                    #{entry.rank}
                  </Typography>
                )}
                <Typography variant="body1" fontWeight={isCurrentUser(entry) ? 'bold' : 'normal'}>
                  {entry.username}
                </Typography>
              </Box>
              <Chip label={`${entry.score} pts`} color="primary" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {entry.totalQuestions} questions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Time: {formatTime(entry.timeTakenSeconds)}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table layout
  return (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell align="center" width="10%">
              <Typography variant="subtitle2" fontWeight="bold">
                Rank
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Name
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Score
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Correct
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Time
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayEntries.map((entry) => (
            <TableRow
              key={entry.userId}
              sx={{
                bgcolor: isCurrentUser(entry) ? 'primary.light' : 'inherit',
                '&:hover': {
                  bgcolor: isCurrentUser(entry) ? 'primary.light' : 'action.hover',
                },
              }}
            >
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {entry.rank <= 3 ? (
                    getMedalIcon(entry.rank)
                  ) : (
                    <Typography variant="body1" fontWeight={isCurrentUser(entry) ? 'bold' : 'normal'}>
                      {entry.rank}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={isCurrentUser(entry) ? 'bold' : 'normal'}>
                  {entry.username}
                  {isCurrentUser(entry) && (
                    <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight="bold">
                  {entry.score}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1">
                  {entry.totalQuestions} questions
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1">{formatTime(entry.timeTakenSeconds)}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LeaderboardTable;
